import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLanguage } from '@/context/LanguageContext';
import { Crown, Sparkles, X, Check, Star } from 'lucide-react';

export const VipTrialOfferModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { startTrial } = useSubscription();
  const { isSpanish } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStartTrial = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await startTrial();
      onClose();
      // Show success message or redirect
    } catch (err) {
      setError(err.message || (isSpanish ? 'Error al iniciar la prueba' : 'Failed to start trial'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaybeLater = () => {
    onClose();
  };

  const benefits = isSpanish ? [
    'Acceso ilimitado a más de 3,400 frases',
    'Sin límite diario - explora todo lo que quieras',
    'Traducciones completas en español',
    'Nuevas frases agregadas regularmente',
    'Cancela en cualquier momento'
  ] : [
    'Unlimited access to 3,400+ phrases',
    'No daily limit - explore all you want',
    'Full Spanish translations',
    'New phrases added regularly',
    'Cancel anytime'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-w-[95vw]" data-testid="vip-trial-modal">
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Crown className="h-7 w-7 text-white" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl">
            {isSpanish ? '¡Bienvenido! 🎉' : 'Welcome! 🎉'}
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {isSpanish 
              ? '¡Prueba VIP GRATIS por 7 días!'
              : 'Try VIP FREE for 7 days!'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-3 space-y-3">
          {/* Benefits list */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
            <h4 className="font-semibold flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 text-yellow-500" />
              {isSpanish ? 'Beneficios VIP:' : 'VIP Benefits:'}
            </h4>
            <ul className="space-y-1">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* Pricing note */}
          <div className="text-center text-xs sm:text-sm text-muted-foreground">
            <p>
              {isSpanish 
                ? 'Después de 7 días: $1.99/mes'
                : 'After 7 days: $1.99/month'}
            </p>
            <p className="text-xs mt-0.5 opacity-75">
              {isSpanish 
                ? 'Sin cargo hoy • Cancela cuando quieras'
                : 'No charge today • Cancel anytime'}
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-500 text-center">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button 
            onClick={handleStartTrial} 
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="start-trial-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {isLoading 
              ? (isSpanish ? 'Iniciando...' : 'Starting...') 
              : (isSpanish ? 'Comenzar Prueba Gratis' : 'Start Free Trial')}
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleMaybeLater}
            className="w-full"
            size="sm"
            data-testid="maybe-later-btn"
          >
            {isSpanish ? 'Quizás después' : 'Maybe later'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VipTrialOfferModal;
